/*
 * The Credit Operations screen's state.
 *
 * Credits are money owed in both directions: `spendable` is what an organizer has paid for, and
 * `debtCredits` is what the platform has already delivered and not been paid for. A screen that
 * shows one without the other reads as healthy when it is not, so both travel together everywhere
 * here.
 *
 * Approving a top-up and granting credits are the two actions that create value out of nothing, so
 * each carries a reason the server records and neither is reachable without one.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, superadminApi } from '../../../api';
import type { CreditLedgerEntryDto, CreditLotDto, PlatformCreditOverviewDto } from '../../../api';

export interface CreditOperationsState {
  overview: PlatformCreditOverviewDto | null;
  loading: boolean;
  loadError: string;
  error: string;
  busy: string;
  confirmTopup: (id: string) => Promise<void>;
  rejectTopup: (id: string, reason: string) => Promise<void>;
  /**
   * `validDays` is not optional here although the DTO allows it.
   *
   * The endpoint refuses a grant that names neither a package nor an explicit validity, and this
   * screen sent `{ credits, note }` — so its primary action answered 400 every time it was pressed,
   * for as long as it has existed. The type is what stops that being possible again.
   */
  grant: (ownerEmail: string, credits: number, note: string, validDays: number) => Promise<void>;
  /*
   * The three the rebuilt screen could not reach. They are the remedies rather than the routine:
   * the overview says an organizer owes credits and holds lots, and until now SuperAdmin could
   * see both and act on neither.
   */
  /** One organizer's lots and ledger — the overview carries counts, not the lots themselves. */
  openOwner: (ownerEmail: string) => Promise<void>;
  closeOwner: () => void;
  ownerDetail: OwnerCreditDetail | null;
  ownerBusy: boolean;
  /** Moves what an organizer owes. A negative delta writes it off; both directions need a reason. */
  adjustDebt: (ownerEmail: string, delta: number, reason: string) => Promise<void>;
  /** Pushes one lot's expiry out. The reason is recorded against the lot, not the organizer. */
  extendLot: (lotId: string, expiresAt: string, reason: string) => Promise<void>;
  reload: () => Promise<void>;
}

export interface OwnerCreditDetail {
  ownerEmail: string;
  lots: CreditLotDto[];
  ledger: CreditLedgerEntryDto[];
}

export function useCreditOperations(token: string): CreditOperationsState {
  const [overview, setOverview] = useState<PlatformCreditOverviewDto | null>(null);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [ownerDetail, setOwnerDetail] = useState<OwnerCreditDetail | null>(null);
  const [ownerBusy, setOwnerBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setOverview(await superadminApi.getCreditOverview(token));
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดข้อมูลเครดิตไม่สำเร็จ');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const run = useCallback(async (key: string, fn: () => Promise<unknown>, fallback: string) => {
    setBusy(key);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusy('');
    }
  }, [load]);

  return {
    overview,
    loading: overview === null && !loadError,
    loadError, error, busy,
    confirmTopup: (id) => run(id, () => superadminApi.confirmCreditTopup(token, id), 'ยืนยันการเติมเครดิตไม่สำเร็จ'),
    rejectTopup: (id, reason) => run(id, () => superadminApi.rejectCreditTopup(token, id, reason), 'ปฏิเสธการเติมเครดิตไม่สำเร็จ'),
    grant: (ownerEmail, credits, note, validDays) =>
      run(
        ownerEmail,
        () => superadminApi.grantCredits(token, ownerEmail, { credits, note, validDays }),
        'ให้เครดิตไม่สำเร็จ',
      ),
    ownerDetail, ownerBusy,
    openOwner: async (ownerEmail) => {
      setOwnerBusy(true);
      setError('');
      /*
       * Cleared first, not merged. The drawer is one component reused for every organizer, and
       * leaving the previous one's lots on screen while these load would show one person's
       * credits under another person's name.
       */
      setOwnerDetail(null);
      try {
        const detail = await superadminApi.getOwnerCredits(token, ownerEmail);
        setOwnerDetail({ ownerEmail, lots: detail.lots ?? [], ledger: detail.ledger ?? [] });
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'โหลดล็อตเครดิตของผู้จัดไม่สำเร็จ');
      } finally {
        setOwnerBusy(false);
      }
    },
    closeOwner: () => setOwnerDetail(null),
    adjustDebt: async (ownerEmail, delta, reason) => {
      await run(ownerEmail, () => superadminApi.adjustCreditDebt(token, ownerEmail, delta, reason), 'ปรับยอดค้างชำระไม่สำเร็จ');
    },
    extendLot: async (lotId, expiresAt, reason) => {
      await run(lotId, () => superadminApi.extendCreditLot(token, lotId, expiresAt, reason), 'ต่ออายุล็อตไม่สำเร็จ');
    },
    reload: load,
  };
}
